/**
 * Operations (ROADMAP Phase 7 groundwork, migration 0057): is every house ready for its next
 * guest? One row per confirmed check-in in the next fourteen days with the open turnover work
 * and the blocking tickets standing in its way, then the ops tasks themselves. Readiness is the
 * view's (`derived.turnover_readiness`), stated in `calc_method`; this screen decides nothing.
 * A person adds and completes tasks; the Ops & Maintenance agent, when it is on, recommends
 * and escalates beside them.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, type OpsTaskJson, type Property, type ReadinessJson } from '../api.js';
import { EntityPicker, useEntityId } from '../components/EntityPicker.js';

const KINDS = ['turnover', 'inspection', 'supplies', 'other'] as const;

export function OperationsPage(): ReactNode {
  const entityId = useEntityId();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [showDone, setShowDone] = useState(false);

  const readiness = useQuery({
    queryKey: ['ops', 'readiness', entityId],
    enabled: entityId !== null,
    queryFn: () =>
      api.get<{ readiness: ReadinessJson[]; calc_method: string | null }>(
        `/api/ops/readiness?entity=${entityId}`,
      ),
  });
  const tasks = useQuery({
    queryKey: ['ops', 'tasks', entityId, showDone],
    enabled: entityId !== null,
    queryFn: () =>
      api.get<{ tasks: OpsTaskJson[] }>(`/api/ops/tasks?entity=${entityId}&open=${!showDone}`),
  });
  const properties = useQuery({
    queryKey: ['properties'],
    queryFn: () => api.get<{ properties: Property[] }>('/api/properties'),
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['ops'] });
  };
  const create = useMutation({
    mutationFn: (body: unknown) => api.post<{ task: OpsTaskJson }>('/api/ops/tasks', body),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (err: Error) => setError(err.message),
  });
  const update = useMutation({
    mutationFn: (input: { id: string; body: unknown }) =>
      api.patch<{ task: OpsTaskJson }>(`/api/ops/tasks/${input.id}`, input.body),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (err: Error) => setError(err.message),
  });

  const onCreate = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const text = (name: string): string => String(data.get(name) ?? '').trim();
    const due = text('dueAt');
    create.mutate({
      propertyId: text('property'),
      kind: text('kind'),
      title: text('title'),
      dueAt: new Date(due).toISOString(),
      assignee: text('assignee') === '' ? null : text('assignee'),
    });
    form.reset();
  };

  const entityProperties = (properties.data?.properties ?? []).filter(
    (p) => p.entity_id === entityId,
  );
  const when = (iso: string): string => new Date(iso).toLocaleString();

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Operations</h1>
          <p className="subtitle">
            Is every house ready for its next guest? Each check-in in the next two weeks with the
            turnover work still open and the tickets that would block it. Tickets themselves are on{' '}
            <Link to="/maintenance">Maintenance</Link>.
          </p>
        </div>
        <EntityPicker />
      </header>

      {error && <p className="error">{error}</p>}

      <section className="card">
        <h2>Readiness, next 14 days</h2>
        {readiness.data && readiness.data.readiness.length === 0 && (
          <p className="muted">No confirmed check-ins in the next fourteen days.</p>
        )}
        {readiness.data && readiness.data.readiness.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Check-in</th>
                  <th className="num">In</th>
                  <th className="num">Open turnover tasks</th>
                  <th className="num">Blocking tickets</th>
                  <th className="num">Other open tickets</th>
                  <th>Ready</th>
                </tr>
              </thead>
              <tbody>
                {readiness.data.readiness.map((r) => (
                  <tr key={r.reservation_id}>
                    <td>{r.property_name}</td>
                    <td>{r.check_in}</td>
                    <td className="num">
                      {r.days_until_check_in === 0 ? 'today' : `${r.days_until_check_in} d`}
                    </td>
                    <td className="num">{r.open_turnover_tasks}</td>
                    <td className="num">{r.blocking_tickets}</td>
                    <td className="num">{r.other_open_tickets}</td>
                    <td>
                      <span className={`chip ${r.ready ? 'chip-ok' : 'chip-attention'}`}>
                        {r.ready ? 'READY' : 'NOT READY'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {readiness.data?.calc_method && <p className="muted small">{readiness.data.calc_method}</p>}
      </section>

      <section className="card">
        <h2>Add a task</h2>
        <form onSubmit={onCreate} className="filters">
          <select name="property" required defaultValue="">
            <option value="" disabled>
              Property
            </option>
            {entityProperties.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <select name="kind" defaultValue="turnover">
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
          <input name="title" placeholder="What needs doing" required maxLength={200} />
          <input name="dueAt" type="datetime-local" required />
          <input name="assignee" placeholder="Who (optional)" maxLength={200} />
          <button type="submit" disabled={create.isPending || entityProperties.length === 0}>
            Add task
          </button>
        </form>
      </section>

      <section className="card">
        <h2>
          Tasks{' '}
          <label className="small muted" style={{ fontWeight: 400 }}>
            <input
              type="checkbox"
              checked={showDone}
              onChange={(e) => setShowDone(e.target.checked)}
            />{' '}
            show done and skipped
          </label>
        </h2>
        {tasks.data && tasks.data.tasks.length === 0 && <p className="muted">Nothing open.</p>}
        {tasks.data && tasks.data.tasks.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Task</th>
                  <th>Kind</th>
                  <th>Due</th>
                  <th>Who</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {tasks.data.tasks.map((t) => (
                  <tr key={t.id}>
                    <td>{t.property_name}</td>
                    <td>{t.title}</td>
                    <td>{t.kind}</td>
                    <td
                      className={
                        t.status === 'open' && new Date(t.due_at) < new Date() ? 'error' : ''
                      }
                    >
                      {when(t.due_at)}
                    </td>
                    <td>{t.assignee ?? '—'}</td>
                    <td>
                      <span className={`chip ${t.status === 'done' ? 'chip-ok' : ''}`}>
                        {t.status}
                      </span>
                    </td>
                    <td>
                      {t.status === 'open' && (
                        <>
                          <button
                            type="button"
                            className="link"
                            onClick={() => update.mutate({ id: t.id, body: { status: 'done' } })}
                          >
                            done
                          </button>{' '}
                          <button
                            type="button"
                            className="link"
                            onClick={() => update.mutate({ id: t.id, body: { status: 'skipped' } })}
                          >
                            skip
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
